import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Company } from '@app/models/company.model';
import { CustomWindow } from '@app/models/custom-window.model';
import { User } from '@app/models/user/user.model';
import { environment } from 'environments/environment';
import { Globals } from '../globals/globals';

declare var window: CustomWindow;

interface IntercomAvatar {
  initials?: string;
  color?: string;
  image_urls?: {
    mini: string;
    mini_x2: string;
    normal: string;
    normal_x2: string;
    large: string;
  };
  image_url?: string;
}

interface IntercomCompany {
  id: string;
  name?: string;
  'Company State'?: string;
}

interface IntercomSettingsMinimal {
  app_id: string;
  user_id?: string;
  email?: string;
  name?: string;
  avatar?: IntercomAvatar;
  company?: IntercomCompany;
  intercom_user_jwt?: string;
  hide_default_launcher?: boolean;
  environment?: string;
  'Access Level'?: string;
  'User State'?: string;
}

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private intercomInitializedForUserId: string | null = null;
  private intercomInitPendingForUserId: string | null = null;
  private intercomBootInProgress = false;

  constructor(
    private httpClient: HttpClient,
    private globals: Globals
  ) {}

  init(user: User): void {
    if (!environment.intercom.enabled) {
      return;
    }
    if (!user || !user.id || !this.globals.company) {
      return;
    }

    const authenticatedUser: User = user;
    const authenticatedCompany: Company = this.globals.company;
    const authenticatedUserIdAsString = String(authenticatedUser.id);

    if (this.intercomInitializedForUserId === authenticatedUserIdAsString) {
      return;
    }
    if (this.intercomInitPendingForUserId === authenticatedUserIdAsString || this.intercomBootInProgress) {
      return;
    }

    this.intercomInitPendingForUserId = authenticatedUserIdAsString;
    this.intercomBootInProgress = true;

    this.httpClient.get('/api/intercom/token', { responseType: 'text' })
      .subscribe({
        next: (intercomUserJwt: string) => {
          const accessLevelJoined = Array.isArray(authenticatedUser.roles)
            ? authenticatedUser.roles.map(r => r.name).join('').trim()
            : '';

          const intercomSettings: IntercomSettingsMinimal = {
            app_id: environment.intercom.id,
            user_id: authenticatedUserIdAsString,
            email: authenticatedUser.email,
            name: `${authenticatedUser.firstName} ${authenticatedUser.lastName}`,
            avatar: {
              image_url: authenticatedUser.imageUrl
            },
            company: {
              id: String(authenticatedCompany.id),
              name: authenticatedCompany.name,
              'Company State': (authenticatedCompany as any).state ? String((authenticatedCompany as any).state) : undefined
            },
            'Access Level': accessLevelJoined,
            'User State': (authenticatedUser as any).userState,
            environment: window.location.hostname,
            intercom_user_jwt: intercomUserJwt
          };

          window.Intercom('boot', intercomSettings);
          this.intercomInitializedForUserId = authenticatedUserIdAsString;
        },
        error: () => {
          this.intercomInitPendingForUserId = null;
        },
        complete: () => {
          this.intercomInitPendingForUserId = null;
          this.intercomBootInProgress = false;
        }
      });
  }

  update(): void {
    if (!this.isReady()) { return; }
    window.Intercom('update');
  }

  show(): void {
    if (!this.isReady()) { return; }
    window.Intercom('show');
  }

  hide(): void {
    if (!this.isReady()) { return; }
    window.Intercom('hide');
  }

  showWidget(): void { 
    this.show(); 
  }

  hideWidget(): void {
    this.hide(); 
  }

  showMessages(): void {
    if (!this.isReady()) { return; }
    window.Intercom('showMessages');
  }

  showNewMessage(initialMessage?: string): void {
    if (!this.isReady()) { return; }
    if (initialMessage) {
      window.Intercom('showNewMessage', initialMessage);
      return;
    }
    window.Intercom('showNewMessage');
  }

  trackEvent(eventName: string, metadata?: Record<string, unknown>): void {
    if (!this.isReady() || !eventName) { return; }
    if (metadata) {
      window.Intercom('trackEvent', eventName, metadata);
      return;
    }
    window.Intercom('trackEvent', eventName);
  }

  getVisitorId(): string {
    if (!this.isReady()) { return ''; }
    return window.Intercom('getVisitorId');
  }

  startTour(tourId: number): void {
    if (!this.isReady()) { return; }
    window.Intercom('startTour', tourId);
  }

  initOnShowCallback(): void {
    if (!this.isReady()) { return; }
    window.Intercom('onShow', () => {});
  }

  initUnreadCountChanged(): void {
    if (!this.isReady()) { return; }
    window.Intercom('onUnreadCountChange', (_unreadCount: number) => {});
  }

  shutdown(): void {
    try {
      window.Intercom('hide');
      window.Intercom('shutdown');
    } finally {
      this.intercomInitializedForUserId = null;
      this.intercomInitPendingForUserId = null;
      this.intercomBootInProgress = false;
    }
  }

  isReady(): boolean {
    return !!this.intercomInitializedForUserId && !this.intercomBootInProgress;
  }
}
